/* eslint-disable jsx-a11y/anchor-has-content */
import React from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import CardVerifyIcon from 'assets/icons/CardVerfiyIcon'
import { Button } from 'components/UI/button/button'
import { MaskedInput } from 'components/UI/input/masked-input'
import SubBody from 'components/UI/sub-body/sub-body'
import IOSDateTimePicker from 'components/UI/wheel-picker/IOSDateTimePicker'
import dayjs from 'dayjs'
import { FC, Fragment, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLazySendGiftQuery, useVerifyGiftMutation } from 'redux/api/send-gift.api'
import { mobilePhoneSelector } from 'redux/store/slices/application-slice'
import tw from 'twin.macro'

import { createPhoneSchema } from '../../../share/schemas/phone.schema'
import OtpVerify from '../otp-verify'

interface Props {
  uuid: string
  expiry_date?: string
  onClose: () => void
  refetchProducts: any
}

type GiftType = 'now' | 'scheduled'

const MAX_CONGRATULATION_LENGTH = 300

const GiftCardFields: FC<Props> = ({ uuid, expiry_date, onClose, refetchProducts }) => {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const stored_mobile_phone = useSelector(mobilePhoneSelector)

  const [giftType, setGiftType] = useState<GiftType>('now')
  const [dateOfGift, setDateOfGift] = useState<Date | null>(null)
  const [message, setMessage] = useState('')

  const [fetchSendGift, { isSuccess, isLoading, error: sendGiftError }] = useLazySendGiftQuery()
  const [fetchVerifyGift, { isSuccess: verifyGiftSuccess, isLoading: verifyGifyLoading, error: verfiyGifyError }] =
    useVerifyGiftMutation()

  const phoneSchema = useMemo(() => createPhoneSchema(t), [i18n.language])

  const {
    control,
    formState: { errors, isValid },
    watch,
    setError
  } = useForm({
    defaultValues: { mobile_phone: '', code: '' },
    resolver: yupResolver(phoneSchema),
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  const phone = watch('mobile_phone').replace(/[()\s]/g, '')
  const code = watch('code')

  const maxDate = useMemo(() => {
    const threeDaysLater = dayjs().add(3, 'day').toDate()
    if (!expiry_date) return threeDaysLater
    const parsed = dayjs(expiry_date, 'MM/YYYY').endOf('month').toDate()
    return parsed < threeDaysLater ? parsed : threeDaysLater
  }, [expiry_date])

  const canSubmit = isValid && (giftType === 'now' || dateOfGift !== null)

  const handleSubmit = async () => {
    try {
      await fetchSendGift({ uuid, mobile_phone: phone }).unwrap()
    } catch (error) {
      const typedErr = error as { data: { error: { code: string; description: any } }; status: number }
      const { code, description } = typedErr.data.error
      if (code === 'SELF_USER_GIVE_CARD_ERROR') {
        setError('mobile_phone', { type: 'validate', message: description })
      } else if (code === 'INVALID_DATA') {
        setError('mobile_phone', { type: 'validate', message: t('error_invalid_phone', { ns: 'gift' }) })
      }
    }
  }

  const handleOtpVerify = async () => {
    fetchVerifyGift({
      uuid,
      mobile_phone: phone,
      code,
      ...(giftType === 'scheduled' && dateOfGift
        ? {
            date_of_gift: dayjs(dateOfGift).format('YYYY-MM-DDTHH:mm:ssZ'),
            message: message || undefined
          }
        : {})
    })
  }

  useEffect(() => {
    verifyGiftSuccess && refetchProducts()
  }, [verifyGiftSuccess])

  if (verifyGiftSuccess) {
    const isScheduled = giftType === 'scheduled' && dateOfGift
    return (
      <div className='sm:max-w-[422px] w-full flex flex-col items-center justify-center py-4 space-y-4'>
        <CardVerifyIcon />
        <p className='text-[22px] font-semibold text-center'>
          {isScheduled ? t('scheduled_success', { ns: 'gift' }) : t('success', { ns: 'gift' })}
        </p>
        <p className='text-center'>
          {isScheduled
            ? t('scheduled_success_desc', {
                ns: 'gift',
                datetime: dayjs(dateOfGift).format('DD.MM.YYYY HH:mm'),
                number: watch('mobile_phone')
              })
            : `${t('gift_other', { ns: 'gift', number: watch('mobile_phone') })} ${t('gift_other_desc', {
                ns: 'gift'
              })}`}
        </p>
        <Button variant='gradient' onClick={onClose}>
          {t('close', { ns: 'gift' })}
        </Button>
      </div>
    )
  }

  return (
    <div tw='sm:max-w-[422px] w-full py-4 space-y-4'>
      <p tw='text-s18 font-bold'>{isSuccess ? t('confirm', { ns: 'gift' }) : t('enter_phone', { ns: 'gift' })}</p>

      {isSuccess ? (
        <>
          <div tw='flex gap-2 p-3 rounded-s14 bg-primary border border-yellow-400'>
            <span tw='text-yellow-500 text-s16 leading-none'>⚠</span>
            <p tw='text-sm text-secondary'>{t('warning_text', { ns: 'gift' })}</p>
          </div>
          <OtpVerify
            phone={stored_mobile_phone}
            control={control}
            name='code'
            isOtpValid={code.length === 4}
            isLoading={verifyGifyLoading}
            onOtpVerify={handleOtpVerify}
            errorMsg={
              (verfiyGifyError as any)?.data?.error?.code === 'INVALID_OTP'
                ? t('error_incorrect_code', { ns: 'gift' })
                : (verfiyGifyError as any)?.data?.error?.description
            }
          />
        </>
      ) : (
        <Fragment>
          <div>
            <MaskedInput
              control={control}
              name={'mobile_phone'}
              label={t('mobile_phone', { ns: 'auth' })}
              mask={'+7(999)999 99 99'}
              placeholder={'+7 ( _ _ _ ) _ _ _  _ _  _ _'}
              error={errors.mobile_phone?.message}
            />
          </div>

          {/* Gift type radio buttons */}
          <div tw='flex gap-3'>
            {(['now', 'scheduled'] as GiftType[]).map(type => (
              <div
                key={type}
                tw='flex items-center gap-2 cursor-pointer flex-1 bg-primary rounded-s14 p-3'
                style={{ border: giftType === type ? '2px solid #4EBC73' : '2px solid transparent' }}
                onClick={() => {
                  setGiftType(type)
                  if (type === 'now') {
                    setDateOfGift(null)
                    setMessage('')
                  }
                }}
              >
                <span tw='text-s14 font-medium'>{t(type, { ns: 'gift' })}</span>
              </div>
            ))}
          </div>

          {/* Scheduled gifting fields */}
          {giftType === 'scheduled' && (
            <div tw='space-y-3'>
              <div>
                <p tw='text-s14 text-secondary mb-1'>{t('execution_date_label', { ns: 'gift' })}</p>
                <IOSDateTimePicker value={dateOfGift} onChange={setDateOfGift} maxDate={maxDate} />
              </div>

              <div>
                <p tw='text-s14 text-secondary mb-1'>{t('congratulation_label', { ns: 'gift' })}</p>
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value.slice(0, MAX_CONGRATULATION_LENGTH))}
                  placeholder={t('congratulation_placeholder', { ns: 'gift' }) as string}
                  rows={4}
                  tw='w-full px-5 py-3 rounded-[14px] border border-transparent bg-input text-s14 outline-none focus-within:border-input-active transition-colors resize-none'
                />
                <p tw='text-s12 text-secondary text-right mt-1'>
                  {t('chars_left', { ns: 'gift', count: MAX_CONGRATULATION_LENGTH - message.length })}
                </p>
              </div>
            </div>
          )}

          <Button variant='gradient' disabled={!canSubmit} onClick={handleSubmit} loading={isLoading}>
            {t('card.continue', { ns: 'cabinet' })}
          </Button>

          <div>
            <SubBody twStyle={tw`text-secondary pt-6`}>
              <Trans
                components={{
                  link1: (
                    <a
                      onClick={() =>
                        window.open(
                          'https://bankffin.kz/storage/docs/DW6oJlZFR8SQOYyRgJuIdKMYLXAj4M7qbhtMzdma.pdf',
                          '_self'
                        )
                      }
                      href='https://bankffin.kz/storage/docs/DW6oJlZFR8SQOYyRgJuIdKMYLXAj4M7qbhtMzdma.pdf'
                      tw='underline font-bold'
                    />
                  )
                }}
              >
                {t('agreement', { ns: 'auth' })}
              </Trans>
            </SubBody>
          </div>
        </Fragment>
      )}
    </div>
  )
}

GiftCardFields.displayName = 'GiftCardFields'
export default GiftCardFields
